<template>
  <div
    class="wrapper"
    style="position: absolute; top: 0; max-width: calc(100% - 40px)"
  >
    <Loader :is-visible="loading" />

    <div v-if="error">
      <div class="card card-inverse card-danger text-center">
        <div class="card-block">
          <blockquote class="card-blockquote">
            Une erreur est survenue lors du chargement de la fiche client...
          </blockquote>
        </div>
      </div>
    </div>

    <div v-if="client" class="animated fadeIn pr-2">
      <ClientDetailsHeader :client="client" />

      <div v-if="client.status === 'suspended'">
        <div class="card card-inverse card-danger text-center">
          <div class="card-block">
            <blockquote class="card-blockquote">
              Le client est bloqué, il ne peut plus accéder à son compte.
            </blockquote>
          </div>
        </div>
      </div>

      <ClientDetailsSection id="global" title="Général">
        <ClientGlobalTab
          :client="client"
          :commercials="commercials"
          :disabled-dates="disabledDates"
          @edit="editClient"
          @editNotes="handleEditNotes"
          @reload="refreshClient"
          @editOrderDescription="updateOrderDescription"
          @deleteOrderDescription="deleteOrderDescription"
          @addOrderDescription="addOrderDescription"
        />
      </ClientDetailsSection>

      <ClientDetailsSection id="notebook" title="Notebook">
        <NotebookCard
          :data="notebook"
          :client="client"
          @noteAdded="notebookNoteAdded"
          @noteUpdated="notebookNoteUpdated"
          @noteDeleted="notebookNoteDeleted"
        />
      </ClientDetailsSection>

      <ClientDetailsSection id="orders" title="Commandes">
        <ClientOrdersTab
          :client="client"
          :notebook="notebook"
          @orderLoading="orderLoading"
          @newOrder="refreshClient(true)"
          @notebookNoteAdded="notebookNoteAdded"
          @notebookNoteUpdated="notebookNoteUpdated"
          @notebookNoteDeleted="notebookNoteDeleted"
        />
      </ClientDetailsSection>

      <ClientDetailsSection id="payments" title="Paiements">
        <ClientPaymentsTab :client="client" @reload="refreshClient(true)" />
      </ClientDetailsSection>

      <ClientDetailsSection
        v-if="!client.isB2B && !client.marketEstablishment"
        id="b2c2b"
        title="B2C2B"
      >
        <ClientB2c2bTab :client="client" :commercials="commercials" />
      </ClientDetailsSection>

      <ClientDetailsSection id="tracking" title="Suivi">
        <MarketingTab :marketings="marketings" @create="createMarketing" />

        <ClientTrackingTab :client="client" @edit="editClient" />
      </ClientDetailsSection>

      <ClientDetailsSection id="sponsorships" title="Parrainages">
        <ClientSponsorshipsTab :client="client" />
      </ClientDetailsSection>

      <ClientDetailsSection
        id="saps"
        title="Attestation Service à la personne (SAP)"
      >
        <ClientSapsTab :client="client" />
      </ClientDetailsSection>
    </div>
  </div>
</template>

<script>
import moment from "moment";

import Loader from "@/components/utils/Loader";

import ClientDetailsHeader from "@/components/client/ClientDetailsHeader";
import ClientDetailsSection from "@/components/client/ClientDetailsSection";
import ClientGlobalTab from "@/components/client/ClientGlobalTab";
import ClientOrdersTab from "@/components/client/ClientOrdersTab";
import ClientPaymentsTab from "@/components/client/ClientPaymentsTab";
import ClientTrackingTab from "@/components/client/ClientTrackingTab";
import ClientSponsorshipsTab from "@/components/client/ClientSponsorshipsTab";
import ClientSapsTab from "@/components/client/ClientSapsTab";
import NotebookCard from "@/components/notebook/NotebookCard";
import MarketingTab from "@/components/marketing/MarketingTab.vue";
import ClientB2c2bTab from "@/components/client/ClientB2c2bTab.vue";

export default {
  components: {
    Loader,
    ClientDetailsHeader,
    ClientDetailsSection,
    ClientGlobalTab,
    ClientOrdersTab,
    ClientPaymentsTab,
    ClientTrackingTab,
    ClientSponsorshipsTab,
    ClientSapsTab,
    NotebookCard,
    MarketingTab,
    ClientB2c2bTab,
  },

  data() {
    return {
      loading: false,
      error: false,

      client: null,
      marketings: [],

      // notebook
      notebook: [],

      // forms options
      commercials: [],
      groups: [],
      disabledDates: {
        to: moment().toDate(),
        from: moment().toDate(),
      },
    };
  },

  watch: {
    $route(route) {
      const { id } = route.params;

      if (id !== this.client?.id) {
        this.client = null;

        this.loadClient();
      }
    },
  },

  created() {
    this.loadClient();
    this.getCommercials();
  },

  methods: {
    updateOrderDescription(template) {
      this.client.orderDescriptions = this.client.orderDescriptions.map(
        (description) =>
          description.id === template.id ? template : description,
      );
    },

    deleteOrderDescription(id) {
      this.client.orderDescriptions = this.client.orderDescriptions.filter(
        (description) => description.id !== id,
      );
    },

    addOrderDescription(template) {
      this.client.orderDescriptions = [
        template,
        ...this.client.orderDescriptions,
      ];
    },

    orderLoading(isLoading) {
      this.loading = isLoading;
    },

    async loadClient() {
      try {
        this.error = false;
        this.loading = true;

        await this.getClient();

        if (!this.client) throw new Error("Loading client error");

        if (this.isAdmin(this.$store.state.user)) {
          this.disabledDates = {
            to: moment(this.client.createdAt)
              .endOf("day")
              .subtract(1, "days")
              .toDate(),
            from: moment().endOf("day").toDate(),
          };
        }
      } catch (e) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },

    async getClient() {
      try {
        const { id } = this.$route.params;
        const { data } = await this.$api.get(`/clients/${id}`);

        this.getNotebook(data.email);

        this.marketings = data.marketings;
        this.client = data;
        this.client.billing = this.client.billing || {};
        this.client.concepts = this.client.concepts || [];
        this.client.contactEmail =
          this.client.contactEmail || this.client.email;

        if (this.client.commercials && !this.client.notes) {
          // Update note
          let notes = "";

          this.client.commercials.forEach(({ commercial }) => {
            if (commercial?.commercial) {
              notes += `${commercial.commercial.pseudo} : ${commercial.commercial.mobile} - `;
            }
          });

          this.client.notes = notes;

          this.handleEditNotes();
        }
      } catch (e) {
        this.client = null;
      }
    },

    async getCommercials() {
      try {
        const { data } = await this.$api.get("/users/commercials/search");

        this.commercials = data.map((user) => ({
          label: user.commercial.pseudo,
          value: user.email,
          mobile: user.commercial.mobile,
          color: user.commercial.color,
          isB2B: user.commercial.isB2B,
          isB2C: user.commercial.isB2C,
        }));
      } catch (e) {
        this.commercials = [];
      }
    },

    async getNotebook(clientEmail) {
      try {
        const { data } = await this.$api.get("/notebook/client", {
          params: {
            email: clientEmail,
          },
        });

        this.notebook = data;
      } catch (e) {
        this.notebook = [];
      }
    },

    handleEditNotes() {
      this.$api.put("/clients", {
        email: this.client.email,
        notes: this.client.notes,
        managerNotes: this.client.managerNotes,
        coachNotes: this.client.coachNotes,
      });
    },

    async editClient({ name, value }) {
      const data = {
        email: this.client.email,
      };

      if (name.includes(".")) {
        const keys = name.split(".");

        data[keys[0]] = { [keys[1]]: value };
      } else {
        data[name] = value;
      }

      try {
        await this.$api.put("/clients", data);

        if (name === "marketEstablishment" && value && this.client.isB2B) {
          await this.$api.post("/clients/commercial", {
            email: this.client.email,
            commercial: "marche@justcoaching.fr",
          });

          window.location.reload();
        }
      } catch (e) {
        if (name === "birthDate") {
          this.client.birthDate = null;
        }

        this.$errorToast("La mise à jour du client a échoué");
      } finally {
        if (name === "trustpilotNotice" && value) {
          this.client.trustpilotNoticeSince = moment().format();
        } else if (name === "trustpilotNotice" && !value) {
          this.client.trustpilotNoticeSince = null;
        }

        if (name === "googleNotice" && value) {
          this.client.googleNoticeSince = moment().format();
        } else if (name === "googleNotice" && !value) {
          this.client.googleNoticeSince = null;
        }
      }
    },

    async refreshClient(clear) {
      this.error = false;
      this.loading = true;

      if (clear) this.client = null;

      await this.getClient();

      if (!this.client) this.error = true;

      this.loading = false;
    },

    notebookNoteAdded(note) {
      this.notebook.unshift(note);
    },

    notebookNoteUpdated(note) {
      this.notebook = this.notebook.map((n) => (n.id === note.id ? note : n));
    },

    notebookNoteDeleted(noteId) {
      this.notebook = this.notebook.filter((note) => note.id !== noteId);
    },

    async createMarketing(dataToSend) {
      try {
        const { data } = await this.$api.post("/marketings/client", {
          marketingData: dataToSend,
          clientEmail: this.client.email,
        });

        this.marketings = [data, ...this.marketings];
        this.$toast.success("La donnée a été crée avec succès");
      } catch (e) {
        this.$toast.error(e);
      }
    },
  },
};
</script>

<style lang="scss">
.events-wrapper {
  background: #384042 !important;
  padding: 5px !important;
}

p.time {
  font-size: 12px !important;
  right: 20px !important;
}

h3.title a {
  color: #444 !important;
}

h2.date {
  display: none;
}

.modal-lg {
  max-width: 1100px;
}

.overflow-auto {
  overflow: auto;
}

.client-details {
  & > .tab-content {
    border: none;
    background: transparent;
    & > .tab-pane {
      padding: 0;
    }
  }
}
</style>
